
import { textSearch } from "@/assets/ts/_utils";
import JsonExcel from "@/components/widgets/tables/JsonExcel.vue";
import { useAdminComarca } from "@/composable/useAdminComarca";
import comarcasData from "@/core/data/comarcas";
import { municipiosComarcas } from "@/core/data/municipiosProvinciasComarcas";
import { titulacionesTodas } from "@/core/data/titulaciones";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { SortProps, sortFn } from "@/core/helpers/sort";
import { ProfesionalEstado } from "@/enums";
import Page from "@/layout/Page.vue";
import {
  AuthModule,
  OrganismosModule,
  ProfesionalesListadoModule,
} from "@/store/modules";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { ErrorMessage, useField } from "vee-validate";
import {
  computed,
  defineComponent,
  onActivated,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useArrayPagination } from "vue-composable";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

export default defineComponent({
  name: "profesionales-listado-ca",
  components: { "download-excel": JsonExcel, Page, ErrorMessage },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const module = getModule(ProfesionalesListadoModule, store);
    const authModule = getModule(AuthModule, store);
    const comarcaId = authModule.user.comarcaId;
    const isAdmin = !comarcaId;

    const organismosModule = getModule(OrganismosModule, store);
    const { selectedComarcaId } = useAdminComarca();
    const comarcaSelectKey = ref(0);
    const loadingComarcas = ref(false);

    const ensureComarcasLoaded = async () => {
      // Optimización: Solo carga si la lista está vacía
      if (organismosModule.organismosListado.length === 0) {
        loadingComarcas.value = true;
        try {
          await organismosModule.fetchAll();
        } finally {
          loadingComarcas.value = false;
        }
      }
    };

    const comarcas = computed(() => {
      const list = organismosModule.organismosListado.map((item) => ({
        id: item.id,
        nombre: item.nombre,
      }));
      list.unshift({ id: "TODAS", nombre: "Todas" });
      return list;
    });

    const csvLabels = {
      nombreComarca: t("csv.solicitudes.familias.comarca"),
      nombre: t("csv.listados.profesionales.nombre"),
      apellidos: t("csv.listados.profesionales.apellidos"),
      dni: t("csv.listados.profesionales.dni"),
      email: t("csv.listados.profesionales.email"),
      telefono: t("csv.listados.profesionales.telefono"),
      fechaNacimientoCsv: t("csv.listados.profesionales.fechaNacimiento"),
      direccion: t("csv.listados.profesionales.direccion"),
      localidad: t("csv.listados.profesionales.localidad"),
      provincia: t("csv.listados.profesionales.provincia"),
      titulacionText: t("csv.solicitudes.profesionales.titulacion"),
      titulacionOtras: t("csv.solicitudes.profesionales.titulacionOtras"),
      carnetConducir: t("csv.listados.profesionales.carnetConducir"),
      vehiculo: t("csv.listados.profesionales.vehiculo"),
    };
    const csvFields = [
      ...(!comarcaId ? ["nombreComarca"] : []),
      "nombre",
      "apellidos",
      "dni",
      "email",
      "telefono",
      "fechaNacimientoCsv",
      "direccion",
      "localidad",
      "provincia",
      "titulacionText",
      "titulacionOtras",
      "carnetConducir",
      "vehiculo",
    ];
    const csvName = "profesionalesListado" + new Date().getTime() + ".xlsx";

    const search = ref("");

    const sortNombreApellidos = function (a, b) {
      let primero = a.nombre + " " + a.apellidos;
      let segundo = b.nombre + " " + b.apellidos;
      if (primero > segundo) {
        return 1;
      }
      if (primero < segundo) {
        return -1;
      }
      return 0;
    };
    const cargaListado = ref(false);

    const fetchData = async () => {
      if (isAdmin && !selectedComarcaId.value) {
        module.setData([]);
        cargaListado.value = false;
        search.value = "";
        currentPage.value = 1;
        return;
      }

      let idToFetch: string | undefined;

      if (isAdmin) {
        idToFetch =
          selectedComarcaId.value === "TODAS"
            ? undefined
            : selectedComarcaId.value!;
      } else {
        idToFetch = comarcaId;
      }

      cargaListado.value = true;
      try {
        await module.fetchProfesionalesCA(idToFetch);
        search.value = "";
        currentPage.value = 1;
      } catch (err) {
        Swal.fire({
          text: t("formularios.profesionalesListado.cargar.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        cargaListado.value = false;
      }
    };

    if (isAdmin) {
      watch(selectedComarcaId, async () => {
        localidad.value = "";
        disponibleComarca.value = "";
        search.value = "";
        await fetchData();
      });
    }

    onBeforeMount(async () => {
      if (isAdmin) {
        ensureComarcasLoaded();
      }
      await fetchData();
    });
    onActivated(async () => {
      if (isAdmin) {
        ensureComarcasLoaded();
        comarcaSelectKey.value++;
      }
      await fetchData();
    });

    const onComarcaVisibleChange = (open: boolean) => {
      if (open && isAdmin) {
        ensureComarcasLoaded();
      }
    };

    onMounted(() => {
      setCurrentPageBreadcrumbs(t("menu.bolsaProfesionalesCA"), [
        t("menu.personas"),
      ]);
    });
    const handleEdit = (index, row) => {
      router.push(`profesionalesCA/${row.id}`);
    };

    const profesionales = computed(() => {
      return module.profesionalesListado?.map((ele) => {
        return {
          ...ele,
          fechaNacimientoCsv: new Date(ele.fechaNacimiento),
          titulacion: ele.titulacion?.map((titulacion) => {
            return (
              titulacionesTodas.find((ele) => ele.id === titulacion)?.text ??
              titulacion
            );
          }),
          nombreCompleto: [ele.nombre, ele.apellidos].join(" "),
          titulacionText:
            ele.titulacion
              ?.map((t) => titulacionesTodas.filter((a) => a.id == t))
              ?.map((t) => t.map((r) => r.text))
              .join(", ") || "",
          titulacionOtras: ele.titulacionOtras,
          nombreComarca: ele.comarca?.nombre,
          comarcasTrabajo: ele.comarcasTrabajo?.map((comarca) => {
            return comarca.id;
          }),
        };
      });
    });

    const sortProps = ref<SortProps>({
      prop: "createdOn",
      order: "descending",
      method: null,
    });

    const filteredCollection = computed(() =>
      profesionales.value
        .slice()
        .sort(sortFn(sortProps))
        .filter((data) => {
          return (
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.nombreCompleto, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.email, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.localidad, search.value) ||
            textSearch(data.comarcasSolicitaTrabajo?.join(","), search.value) ||
            textSearch(data.nombreComarca, search.value)
          );
        })
    );

    const { value: localidad } = useField("localidad");
    const { value: disponibleComarca } = useField("disponibleComarca");

    const profesionalesListado = computed(() => {
      //console.log("profesionales.value", profesionales.value);
      //console.log("disponibleComarca.value", disponibleComarca?.value);
      const filteredBySearch = (profesionales.value ?? [])
        .slice()
        .sort(sortFn(sortProps))
        .filter((data) => {
          return (
            !search.value ||
            textSearch(!comarcaId ? data.nombreComarca : "", search.value) ||
            textSearch(data.nombreCompleto, search.value) ||
            textSearch(data.dni, search.value) ||
            textSearch(data.email, search.value) ||
            textSearch(data.telefono, search.value) ||
            textSearch(data.localidad, search.value) ||
            textSearch(data.comarcasSolicitaTrabajo?.join(","), search.value) ||
            textSearch(data.nombreComarca, search.value)
          );
        });

      if (
        (localidad?.value === undefined || localidad?.value === "") &&
        (disponibleComarca?.value === undefined ||
          disponibleComarca?.value === "")
      ) {
        return filteredBySearch;
      } else {
        // Intersección de registros filtrados por búsqueda y por localidad y disponibilidad para trabajar
        return filteredBySearch?.filter((data) => {
          const matchesLocalidad = localidad?.value
            ? data.localidad === localidad.value
            : true;
          const matchesComarca = disponibleComarca?.value
            ? data.comarcasSolicitaTrabajoIds?.includes(
                disponibleComarca.value as string
              )
            : true;

          return matchesLocalidad && matchesComarca;
        });
      }
    });

    const municipiosComarca = computed(() => {
      if (!isAdmin) {
        return municipiosComarcas.filter(
          (municipio) => municipio.comarcaId === comarcaId
        );
      }

      if (!selectedComarcaId.value || selectedComarcaId.value === "TODAS") {
        return municipiosComarcas;
      }

      return municipiosComarcas.filter(
        (municipio) => municipio.comarcaId === selectedComarcaId.value
      );
    });
    const { result, currentPage, lastPage } = useArrayPagination(
      profesionalesListado,
      { pageSize: 100 }
    );

    return {
      profesionalesListado,
      filteredCollection,
      cargaListado,
      isAdmin,
      csvLabels,
      csvFields,
      csvName,
      handleEdit,
      ProfesionalEstado,
      sortNombreApellidos,
      search,
      sortChange: ({ column, prop, order }) => {
        sortProps.value = { prop, order, method: column.sortMethod };
      },
      result,
      currentPage,
      lastPage,
      handleCurrentPageChange: (page: number) => (currentPage.value = page),
      municipiosComarca,
      localidad,
      disponibleComarca,
      comarcasFiltro: comarcasData, // para no chocar con las comarcas del selector
      selectedComarcaId,
      comarcas,
      loadingComarcas,
      onComarcaVisibleChange,
      comarcaSelectKey,
    };
  },
});
